/**
 * Klasse PORTAL_VIEW_HISTORY — Intra-Portal-Views in der Browser-History.
 *
 * Polar:
 *   VERBOTEN: View-Wechsel (ARKO/Diktat/Dokumente/…) nur per React-State,
 *   sodass Zurück die Firmen-Homepage trifft.
 *   ERLAUBT: pushState + Hash, Zurück = vorherige Portal-Ansicht.
 *   Erster Einstieg Website→Portal: replaceState (ein Zurück zur Website bleibt).
 *   Kein history.forward-Trap, kein disable-back.
 *
 * Hash bleibt client-seitig (nginx try_files / → index.html unverändert).
 */
import type { PortalView } from './portalNavigation';

export const PORTAL_HISTORY_STATE_KEY = 'portalView' as const;

/** Views, die einen History-Eintrag tragen (Aliases werden kanonisiert). */
export const PORTAL_HISTORY_VIEWS: readonly PortalView[] = [
  'home',
  'chat',
  'suche',
  'dokumente',
  'akten',
  'kalender',
  'diktat',
  'bausteine',
  'litigation',
  'contract_generator',
  'document_editor',
  'pdf_tools',
  'gedaechtnis',
  'tasten',
  'feedback',
  'matrix',
  'kanzlei_zugaenge',
] as const;

const HISTORY_VIEW_SET = new Set<string>(PORTAL_HISTORY_VIEWS);

export type PortalHistoryRecordMode = 'push' | 'replace' | 'skip';

export type PortalHistorySource = 'init' | 'navigate' | 'pop';

export function isPortalHistoryView(value: string): value is PortalView {
  return HISTORY_VIEW_SET.has(value);
}

/** Legacy-Aliase: Upload→Diktat, Protokolle→Dokumente. */
export function canonicalizePortalView(view: string): PortalView | null {
  if (view === 'upload') return 'diktat';
  if (view === 'protokolle') return 'dokumente';
  if (view === 'arko') return 'chat';
  if (isPortalHistoryView(view)) return view;
  return null;
}

/**
 * Hash-Parser. Unterstützt #diktat, #/diktat, #v=diktat.
 * Unbekannte Fragmente (z. B. Anker) → null, nicht raten.
 */
export function parsePortalViewHash(hash: string): PortalView | null {
  const raw = String(hash || '').replace(/^#/, '').trim();
  if (!raw) return null;
  let slug = raw;
  if (slug.startsWith('/')) slug = slug.slice(1);
  if (slug.startsWith('v=')) slug = slug.slice(2);
  slug = slug.split(/[/?&]/, 1)[0]?.trim() || '';
  if (!slug) return null;
  return canonicalizePortalView(slug);
}

export function portalViewToHash(view: PortalView): string {
  const canonical = canonicalizePortalView(view) ?? 'home';
  return `#${canonical}`;
}

export function portalHistoryHref(pathname: string, search: string, view: PortalView): string {
  const path = pathname || '/';
  const q = search && search !== '?' ? search : '';
  return `${path}${q}${portalViewToHash(view)}`;
}

/** Query streichen, Hash/View behalten — Login/IDA-Cleanup darf History nicht leeren. */
export function hrefPathAndHash(pathname: string, hash: string): string {
  const h = !hash ? '' : hash.startsWith('#') ? hash : `#${hash}`;
  return `${pathname || '/'}${h}`;
}

export function readPortalViewFromLocation(hash: string, historyState: unknown): PortalView | null {
  const fromHash = parsePortalViewHash(hash);
  if (fromHash) return fromHash;
  if (historyState && typeof historyState === 'object') {
    const raw = (historyState as { [PORTAL_HISTORY_STATE_KEY]?: unknown })[PORTAL_HISTORY_STATE_KEY];
    if (typeof raw === 'string') return canonicalizePortalView(raw);
  }
  return null;
}

/**
 * Wann ein History-Eintrag geschrieben wird.
 * init → replace (Website bleibt ein Zurück).
 * pop → skip (kein Trap).
 * navigate + andere View → push.
 * gleiche View / Alias → skip.
 */
export function portalHistoryRecordMode(opts: {
  previousRecorded: string | null;
  nextView: string;
  source: PortalHistorySource;
}): PortalHistoryRecordMode {
  const next = canonicalizePortalView(opts.nextView);
  if (!next) return 'skip';
  if (opts.source === 'pop') return 'skip';
  if (opts.source === 'init' || opts.previousRecorded == null) return 'replace';
  if (opts.previousRecorded === next) return 'skip';
  return 'push';
}

export function applyPortalViewHistory(
  mode: Exclude<PortalHistoryRecordMode, 'skip'>,
  view: PortalView,
  opts?: { stripSearch?: boolean },
): void {
  if (typeof window === 'undefined') return;
  const canonical = canonicalizePortalView(view);
  if (!canonical) return;
  const search = opts?.stripSearch ? '' : window.location.search;
  const href = portalHistoryHref(window.location.pathname, search, canonical);
  const state = { [PORTAL_HISTORY_STATE_KEY]: canonical };
  try {
    if (mode === 'replace') {
      window.history.replaceState(state, '', href);
    } else {
      window.history.pushState(state, '', href);
    }
  } catch {
    /* ignore */
  }
}
